import React, { useContext, useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import ReactPaginate from 'react-paginate';
import { format } from 'date-fns';
import { ProductContext } from '../../../../store';
import ReportHeader from '../../../../components/Search/ReportHeader';
import ReportSortArrows from '../../../../components/Search/ReportSortArrows';
import DetailSkeleton from '../../../../components/Skeleton/DetailSkeleton';
import TableSkeleton from '../../../../components/Skeleton/TableSkeleton';
import { PostData } from '../../../../services/FetchData';
import { searchZeroReportsAnalyticsUrl } from "../../../../constants/searchReportsAnalyticsApi";
import { buildReportRequestPayload, getActiveWebsiteId } from "../../../../utils/reportUtils";
import { toast } from 'react-toastify';

const ZeroResultQueries = () => {
    const [pageLoader, setPageLoader] = useState(true),
        [tableLoader, setTableLoader] = useState(true),
        [data, setData] = useState([]),
        [totalPageCount, setTotalPageCount] = useState(0),
        [limit, setLimit] = useState(25),
        [page, setPage] = useState(0),
        [sortField, setSortField] = useState(null),
        [sortOrder, setSortOrder] = useState("desc"),
        { dateRange, setDateRange, currentWebsiteId } = useContext(ProductContext);

    useEffect(() => {
        if (getActiveWebsiteId(currentWebsiteId)) {
            getReports();
        }
    }, [dateRange, limit, page, currentWebsiteId, sortField, sortOrder]);

    // Map frontend field names to API field names
    const mapSortFieldToApi = (field) => {
        const fieldMap = {
            "query": "searchQuery",
            "volume": "searchVolume",
            "zeroResultSession": "zeroResultPercentage"
        };
        return fieldMap[field] || field;
    };

    const handleSort = (field, e) => {
        e?.stopPropagation?.();
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("desc");
        }
        setPage(0);
    };
    const setSort = (field, order, e) => {
      e?.stopPropagation?.();
      setPage(0);
      setSortField(field);
      setSortOrder(order);
    };


    const getReports = () => {
        setTableLoader(true);
        const payload = buildReportRequestPayload(currentWebsiteId, dateRange, {
            page: page + 1,
            limit,
            sortField: sortField ? mapSortFieldToApi(sortField) : "",
            sortOrder: sortOrder || "desc",
        });
        if (!payload) {
            setTableLoader(false);
            setPageLoader(false);
            return;
        }
        
        PostData("ms1", searchZeroReportsAnalyticsUrl("searchZeroResult"), payload).then(response => {
            if (response !== "Invalid" && response !== "undefined" && response) {
                if (response.status === "success" && response.data) {
                    const dataArray = Array.isArray(response.data.data) ? response.data.data : [];
                    const transformedData = dataArray.map(item => {
                        return {
                            query: item.searchQuery || "",
                            volume: item.searchVolume || 0,
                            zeroResultSession: typeof item.zeroResultPercentage === 'number' ? item.zeroResultPercentage.toFixed(2) + "%" : "0.00%"
                        };
                    });
                    
                    setData(transformedData);
                    
                    let totalRecord = response.data.total || 0,
                        totalPage = Math.ceil(totalRecord / limit);
                    setTotalPageCount(totalPage);
                } else {
                    toast.error(response.message || "Failed to fetch data");
                    setData([]);
                }
            } else {
                setData([]);
            }
            setTableLoader(false);
            setPageLoader(false);
        }).catch(err => {
            console.error("Error fetching zero result queries:", err);
            setTableLoader(false);
            setPageLoader(false);
        });
    };

    return (
        <div>
            {pageLoader ?
                <DetailSkeleton />
            :
                <React.Fragment>
                    <ReportHeader range={dateRange} onDateChange={(range) => setDateRange(range)} />
                    <div className="tab-content mt-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h4 className="mb-0">Zero-Result Queries Report</h4>
                            <div className="d-flex align-items-center">
                                <label htmlFor="limit" className="me-2">
                                    Items Per Page:
                                </label>
                                <select id="limit" className="form-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))} style={{ width: "100px", fontSize: "12px" }}>
                                    <option value={25}>25</option>
                                    <option value={30}>30</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            {tableLoader ?
                                <TableSkeleton />
                            :
                                <Table className={`table ${data.length > 0 ? "table-striped" : ""} mt-3 custom-bordered-table`}>
                                    <thead>
                                        <tr>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => handleSort("query", e)}>
                                                <div className="sortable-header-inner">
                                                    Search Query
                                                    <ReportSortArrows field="query" activeSortField={sortField} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => handleSort("volume", e)}>
                                                <div className="sortable-header-inner">
                                                    Search Volume
                                                    <ReportSortArrows field="volume" activeSortField={sortField} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                            <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => handleSort("zeroResultSession", e)}>
                                                <div className="sortable-header-inner">
                                                    % Sessions with Zero Results
                                                    <ReportSortArrows field="zeroResultSession" activeSortField={sortField} sortOrder={sortOrder} onSort={setSort} />
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.length === 0 ? (
                                            <tr>
                                                <td colSpan={3}>
                                                    <div className="emptyPlaceholder">
                                                        <div>
                                                            <div>
                                                                <img src={require("./../../../../assets/images/paper-plane-grey.png")} className='img-fluid' alt="No data found" />
                                                            </div>
                                                            <div className="rsInfoMsg disabled">
                                                                <div className="flex-1">No records found.</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            data.map((row, index) => (
                                                <tr key={index}>
                                                    <td>{row.query}</td>
                                                    <td>{row.volume?.toLocaleString() || 0}</td>
                                                    <td>{row.zeroResultSession}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            }
                            {totalPageCount > 1 &&
                                <div className='d-flex justify-content-center mt-5'>
                                    <ReactPaginate
                                        nextLabel=">"
                                        onPageChange={(event) => setPage(event.selected)}
                                        page={page}
                                        pageRangeDisplayed={3}
                                        marginPagesDisplayed={2}
                                        pageCount={totalPageCount}
                                        previousLabel="<"
                                        pageClassName="page-item"
                                        pageLinkClassName="page-link"
                                        previousClassName="page-item"
                                        previousLinkClassName="page-link"
                                        nextClassName="page-item"
                                        nextLinkClassName="page-link"
                                        breakLabel="..."
                                        breakClassName="page-item"
                                        breakLinkClassName="page-link"
                                        containerClassName="pagination"
                                        activeClassName="active"
                                        renderOnZeroPageCount={null}
                                    />
                                </div>
                            }
                        </div>
                    </div>
                </React.Fragment>
            }
        </div>
    );
};

export default ZeroResultQueries;

